import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Snackbar, Alert } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import theme from './theme';
import { AppProvider, useApp } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { Layout, LoadingSpinner } from './components/common';
import ProtectedRoute, { PublicRoute } from './components/common/ProtectedRoute';
import { PERMISSIONS } from './context/AuthContext';

// Pages
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import LeadForm from './pages/LeadForm';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Billing from './pages/Billing';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import DMTemplates from './pages/DMTemplates';
import WarmUpMode from './pages/WarmUpMode';
import FollowupCalendar from './pages/FollowupCalendar';
import Login from './pages/Login';
import Register from './pages/Register';
import Users from './pages/Users';

const AppContent = () => {
  const { loading, snackbar, closeSnackbar } = useApp();

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading WebFlow CRM..." />;
  }

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.DASHBOARD_VIEW]}>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leads"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.LEADS_VIEW]}>
              <Layout>
                <Leads />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leads/new"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.LEADS_CREATE]}>
              <Layout>
                <LeadForm />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leads/:id"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.LEADS_VIEW]}>
              <Layout>
                <LeadDetail />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leads/:id/edit"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.LEADS_EDIT]}>
              <Layout>
                <LeadForm />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.CLIENTS_VIEW]}>
              <Layout>
                <Clients />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/:id"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.CLIENTS_VIEW]}>
              <Layout>
                <ClientDetail />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.BILLING_VIEW]}>
              <Layout>
                <Billing />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.ANALYTICS_VIEW]}>
              <Layout>
                <Analytics />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.SETTINGS_VIEW]}>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.TEMPLATES_VIEW]}>
              <Layout>
                <DMTemplates />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/warmup"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.LEADS_VIEW]}>
              <Layout>
                <WarmUpMode />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.LEADS_VIEW]}>
              <Layout>
                <FollowupCalendar />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.USERS_VIEW]}>
              <Layout>
                <Users />
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Router>
          <AuthProvider>
            <AppProvider>
              <AppContent />
            </AppProvider>
          </AuthProvider>
        </Router>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
